import { useEffect, useState } from "react";
import { getScreenReceptionCommonWord } from "~/utils/receptionUtils";

export type ErrorMessage = {
  title: string;
  content: string;
} | null;

/**
 * 受付失敗時に表示するタイトルと本文を整形する。
 */
const createFailedMessage = (
  t: (k: string) => string,
  content: string
): NonNullable<ErrorMessage> => ({
  title: t("receptionFailed"),
  content,
});

/**
 * 言語設定に応じた受付失敗メッセージを取得し、表示用のタイトルと本文を返す。
 *
 * 戻り値の契約:
 * - 取得処理が完了するまでこのフックは `null` を返します。呼び出し側は
 *   `null` を受け取る可能性があることを前提にガードしてください。
 * - 取得完了後は `{ title: string, content: string }` を返します。`title` や
 *   `content` は空文字列になり得ます（例: フォールバックや取得失敗時）。
 *
 * 例:
 * ```ts
 * const msg = useReceptionFailedMessage(lang, t);
 * if (msg === null) return; // still loading
 * const { title, content } = msg; // safe to use
 * ```
 *
 * 設定取得に失敗した場合は、`title` に翻訳済みの受け付け失敗文言を入れ、
 * `content` は空文字列になります。
 */
export const useReceptionFailedMessage = (
  language: string,
  t: (k: string) => string
): ErrorMessage => {
  const [message, setMessage] = useState<ErrorMessage>(null);

  useEffect(() => {
    let mounted = true;

    const fetch = async () => {
      try {
        const content = await getScreenReceptionCommonWord(language, "receptionFailedMessage");

        if (!mounted) return;

        setMessage(createFailedMessage(t, content));
      } catch {
        if (!mounted) return;

        setMessage(createFailedMessage(t, ""));
      }
    };

    void fetch();

    return () => {
      mounted = false;
    };
  }, [language, t]);

  return message;
};
